import wordlistText from "@cogcoin/genesis/bip39_english.txt?raw";

export const BIP39_WORD_COUNT = 2048;

export const BIP39_WORDS: readonly string[] = Object.freeze(
  wordlistText
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0),
);

if (BIP39_WORDS.length !== BIP39_WORD_COUNT) {
  throw new Error(
    `expected ${BIP39_WORD_COUNT} BIP-39 words from @cogcoin/genesis; got ${BIP39_WORDS.length}`,
  );
}

const wordToIndex: ReadonlyMap<string, number> = (() => {
  const map = new Map<string, number>();
  for (let i = 0; i < BIP39_WORDS.length; i++) {
    map.set(BIP39_WORDS[i]!, i);
  }
  return map;
})();

/// Returns the BIP-39 word at the given 0-based index, or throws if out of range.
export function bip39Word(index: number): string {
  const word = BIP39_WORDS[index];
  if (word === undefined) {
    throw new RangeError(`BIP-39 index out of range: ${index}`);
  }
  return word;
}

/// Returns the BIP-39 index for the given word, or `undefined` if not in the wordlist.
export function bip39Index(word: string): number | undefined {
  return wordToIndex.get(word);
}

/// Returns `true` if the word is in the BIP-39 English wordlist.
export function isBip39Word(word: string): boolean {
  return wordToIndex.has(word);
}

/**
 * Returns up to `max` BIP-39 words that start with `prefix`. The empty
 * prefix returns no matches (autocomplete should not flood the user
 * with the whole wordlist).
 */
export function prefixMatches(prefix: string, max: number): string[] {
  if (prefix.length === 0 || max <= 0) {
    return [];
  }
  // The BIP-39 wordlist is sorted alphabetically; binary-search for the
  // first match and walk forward. 2048 entries is small enough that a
  // linear scan would also work, but the search is trivial.
  let lo = 0;
  let hi = BIP39_WORDS.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if ((BIP39_WORDS[mid]!) < prefix) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  const result: string[] = [];
  for (let i = lo; i < BIP39_WORDS.length && result.length < max; i++) {
    const word = BIP39_WORDS[i]!;
    if (!word.startsWith(prefix)) break;
    result.push(word);
  }
  return result;
}
